/**
 * EventGroupContent.js
 *
 * Content section for event group cards (theater runs, festivals, concert residencies).
 * Layout matches ActivityCard's event-group variant.
 */

import React, { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import EntityService from '../../../services/entityService';
import UitInVlaanderenCredit from '../../shared/UitInVlaanderenCredit';

// Group-type → catalog key stem for the count label (feedCard:
// eventGroupContent.count.<stem>). Types not listed fall back to
// eventGroupContent.count.default.
const GROUP_TYPE_COUNT_KEYS = {
  theater_run: 'theaterRun',
  concert_residency: 'concertResidency',
  comedy_run: 'comedyRun',
  live_performance: 'livePerformance',
  sports_series: 'sportsSeries',
  film_screening: 'filmScreening',
  workshop: 'workshop',
  festival: 'festival',
  family_event: 'familyEvent',
  exhibition: 'exhibition',
  community_event: 'communityEvent',
  nightlife: 'nightlife'
};

const EventGroupContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const { title, location, typeData } = data;
  const {
    groupType,
    eventCount = 0,
    startDate,
    endDate,
    venue,
    city,
    priceMin,
    priceMax,
    price,
    source,
    matchType,
    matchReason,
    matchedArtist,
    matchedEntityId
  } = typeData || {};

  const [addedToFavorites, setAddedToFavorites] = useState(false);
  const [adding, setAdding] = useState(false);

  const showAddFavorite = matchedEntityId && matchType && matchType !== 'direct' && !addedToFavorites;

  const handleAddFavorite = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (adding || addedToFavorites) return;

    setAdding(true);
    const result = await EntityService.saveUserPreference(matchedEntityId, 2);
    setAdding(false);

    if (result.success) {
      setAddedToFavorites(true);
      window.dispatchEvent(new CustomEvent('favorite-artist-added', { detail: { entityId: matchedEntityId, artistName: matchedArtist } }));
    }
  }, [matchedEntityId, matchedArtist, adding, addedToFavorites]);

  const formatPriceRange = () => {
    if (!priceMin) return null;
    if (!priceMax || priceMin === priceMax) {
      return t('eventGroupContent.priceFrom', { price: Math.round(priceMin) });
    }
    return t('eventGroupContent.priceRange', { min: Math.round(priceMin), max: Math.round(priceMax) });
  };

  const getGroupTypeIcon = (type) => {
    const iconNames = {
      'theater_run': 'theater',
      'festival': 'party',
      'concert_residency': 'musicalNote',
      'sports_series': 'trophy',
      'comedy_run': 'theater',
      'family_event': 'userGroup',
      'exhibition': 'paintBrush',
      'live_performance': 'sparkles',
      'film_screening': 'film',
      'workshop': 'bookOpen',
      'community_event': 'mapPin',
      'nightlife': 'moon',
      'multi_day_event': 'calendar',
      'tour': 'microphone'
    };
    return <Icon name={iconNames[type] || 'calendar'} className="w-3 h-3 inline" />;
  };

  // Context-appropriate count label based on group type
  const getCountLabel = () => {
    const isSingleDate = startDate && endDate && startDate === endDate;
    if (isSingleDate) {
      return t('eventGroupContent.count.ticketOption', { count: eventCount });
    }
    const keyStem = GROUP_TYPE_COUNT_KEYS[groupType] || 'default';
    return t(`eventGroupContent.count.${keyStem}`, { count: eventCount });
  };

  const displayLocation = (venue && city) ? `${venue}, ${city}` : location;
  const priceDisplay = formatPriceRange() || price;

  return (
    <div className="flex-grow">
      {/* Title */}
      <h3 className="font-semibold text-content-primary line-clamp-2 mb-2">{title}</h3>

      {/* Location */}
      {displayLocation && (
        <p className="text-sm text-content-secondary mb-2 line-clamp-1">
          <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />{displayLocation}
        </p>
      )}

      {/* Artist match reason takes priority over showtime count */}
      {matchReason ? (
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-xs text-primary font-medium line-clamp-1 flex-1 min-w-0">
            {matchType === 'direct' ? <Icon name="microphone" className="w-3 h-3 inline" /> : <Icon name="link" className="w-3 h-3 inline" />}{' '}{matchReason}
          </p>
          {showAddFavorite && (
            <button
              onClick={handleAddFavorite}
              disabled={adding}
              className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-lighter text-primary hover:bg-primary hover:text-content-inverse transition-colors"
              title={t('eventGroupContent.favorite.addButtonTitle', { matchedArtist })}
              aria-label={t('eventGroupContent.favorite.addButtonTitle', { matchedArtist })}
            >
              <Icon name="plus" className="w-3 h-3" />
              <span className="hidden sm:inline">{t('eventGroupContent.favorite.addButtonLabel')}</span>
            </button>
          )}
          {addedToFavorites && (
            <span className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark">
              <Icon name="check" className="w-3 h-3" />
              <span className="hidden sm:inline">{t('eventGroupContent.favorite.addedLabel')}</span>
            </span>
          )}
        </div>
      ) : !!eventCount && (
        <p className="text-xs text-primary font-medium mb-2 line-clamp-1">
          {getGroupTypeIcon(groupType)}{' '}{getCountLabel()}
          {priceDisplay ? t('eventGroupContent.priceSeparator', { price: priceDisplay }) : ''}
        </p>
      )}

      {/* Source credit required by publiq's terms — per card, because the
          mixed infinite feed has no footer these cards are reliably near. */}
      {source === 'uitdatabank' && <UitInVlaanderenCredit variant="inline" />}
    </div>
  );
};

export default memo(EventGroupContent);
